import { useCallback, useEffect, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { remCalc } from 'foundations-components/transitional/utils/styles';
import { ContentTypes } from 'academy-shared-js/constants/ContentConstants';
import ApiError from '../Errors/ApiError';
import UsageTracker from '../../utils/UsageTracker';
import { BaseContentControlWrapper } from '../ContentControlWrapper';
import { isInViewport } from '../../utils/utils';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ChapterContentWrapper = styled.div.withConfig({
  displayName: "ChapterContent__ChapterContentWrapper",
  componentId: "sc-1v59qvj-0"
})(({
  theme
}) => ({
  fontSize: remCalc('18px'),
  h3: {
    marginTop: remCalc(theme.space['1100'])
  },
  h4: {
    marginTop: remCalc(theme.space['600'])
  },
  img: {
    display: 'block',
    margin: `${remCalc(theme.space['500'])} auto`,
    maxWidth: '100%'
  }
}));
const ChapterContent = ({
  name,
  contents,
  error,
  isLoading,
  contentId,
  contentLanguage,
  trackId,
  lessonId,
  completed,
  advanceToNextContent,
  onComplete
}) => {
  const footerRef = useRef(null);
  const maybeMarkCompleted = useCallback(() => {
    if (!completed && contents) {
      onComplete();
    }
  }, [completed, contents, onComplete]);
  useEffect(() => {
    const checkProgress = () => {
      if (footerRef.current && isInViewport(footerRef.current)) {
        maybeMarkCompleted();
      }
    };
    document.addEventListener('scroll', checkProgress, true);
    checkProgress();
    return () => {
      document.removeEventListener('scroll', checkProgress, true);
    };
  }, [completed, contents, maybeMarkCompleted, onComplete]);
  const handleClickNext = () => {
    maybeMarkCompleted();
    advanceToNextContent();
  };
  const handleClickOnChapterContent = event => {
    const {
      currentTarget: target
    } = Object.assign({}, event); // event bubled up to parent;

    if (target && target.href && target) {
      if (!target.href) {
        return;
      }
      if (!contentId || !trackId && !lessonId) {
        return;
      }
      UsageTracker.track('contentInteraction', {
        action: 'open-chapter-link',
        contentId,
        contentLanguage,
        contentType: ContentTypes.CHAPTER,
        trackId,
        lessonId,
        resourceTitle: target.textContent,
        resourceUrl: target.href
      });
    }
  };
  const renderContent = () => {
    if (isLoading) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      });
    }
    if (error) {
      return /*#__PURE__*/_jsx(ApiError, {
        error: error
      });
    }
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ChapterContentWrapper, {
        dangerouslySetInnerHTML: {
          __html: contents || ''
        },
        "data-test-id": "chapter-html-content",
        onClick: handleClickOnChapterContent
      })
    );
  };
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "chapter-content",
    children: [/*#__PURE__*/_jsxs(View, {
      marginBlockStart: "1000",
      padding: "250",
      children: [/*#__PURE__*/_jsx(View, {
        borderBlockEndColor: "border-core-default",
        borderBlockEndStyle: "solid",
        borderBlockEndWidth: "100",
        marginBlockEnd: "1000",
        paddingBlockEnd: "600",
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          children: name
        })
      }), renderContent()]
    }), /*#__PURE__*/_jsx(BaseContentControlWrapper, {
      children: /*#__PURE__*/_jsx("div", {
        ref: ref => {
          footerRef.current = ref;
        },
        children: /*#__PURE__*/_jsx(Button, {
          onClick: handleClickNext,
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.common.next"
          })
        })
      })
    })]
  });
};
export default ChapterContent;